import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import RefundAPI from "api/faber/RefundAPI";
import type { RefundHistoryProps, RefundItem } from "types/api/v1/refund.type";




function formatMoney(currency = "USD", amount = 0, cents = true) {
  const value = cents ? amount / 100 : amount;
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
}

function formatDateTime(iso?: string) {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function statusColor(status?: string): "success" | "warning" | "default" {
  const s = (status || "").toUpperCase();
  if (s.includes("FULL")) return "success";
  if (s.includes("PARTIAL")) return "warning";
  return "default";
}



export default function RefundHistoryDialog({
  dialogOpen,
  handleDialogClose,
  faberToken,
  customerId,
  paymentIntentId,
  amountsAreCents = true,
}: RefundHistoryProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
const [item, setItem] = useState<RefundItem | null>(null);

useEffect(() => {
  if (!dialogOpen) return;
  if (!customerId) return;
  if (!paymentIntentId) return;

  setLoading(true);
  setError("");

  RefundAPI.getCustomerRefundHistory(
    { faberToken },
    customerId,
    paymentIntentId
  )
    .then((res) => {
      console.log("Refund API response:", res.data);

      // IMPORTANT: cast API array to RefundItem[]
      const list = res.data.data as RefundItem[];

      const found =
        list.find((x) => x.paymentIntentId === paymentIntentId) ?? null;

      setItem(found);
    })
    .catch((err) => {
      console.error("Refund API error:", err);
      setError("Failed to load refund history");
    })
    .finally(() => {
      setLoading(false);
    });
}, [dialogOpen, customerId, paymentIntentId, faberToken]);

  const remaining = useMemo(() => {
    if (!item) return 0;
    return Math.max(0, (item.originalAmount ?? 0) - (item.refundedAmountTotal ?? 0));
  }, [item]);

  return (
    <Dialog
      open={dialogOpen}
      maxWidth="sm"
      fullWidth
      onClose={handleDialogClose}
      aria-labelledby="refund-history-dialog-title"
    >
      <DialogTitle
        id="refund-history-dialog-title"
        sx={{ m: 0, fontWeight: 700, color: "black", fontSize: "21px", mt: "4px", px: 3 }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            Refund history
            <Typography variant="body2" sx={{ color: "text.secondary", wordBreak: "break-all", mt: 0.5 }}>
              PI: {paymentIntentId ?? "-"}
            </Typography>
          </Box>

          <Button onClick={handleDialogClose} sx={{ minWidth: 0 }}>
            ✕
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && !item && (
          <Typography sx={{ py: 2, color: "text.secondary" }}>
            No refund history found for this payment intent.
          </Typography>
        )}

        {!loading && !error && item && (
          <>
            {/* Header summary */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800 }}>
                  {item.catalogName ?? "-"} ×{item.catalogQuantity ?? 1}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  User: {item.userId ?? "-"}
                </Typography>
              </Box>

              <Chip size="small" label={item.status} color={statusColor(item.status)} />
            </Stack>

            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              Purchased: {formatDateTime(item.packPurchase)} • Last refund: {formatDateTime(item.lastRefundAt)}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* KPIs */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={styles.kpiBox}>
                <Typography variant="caption" sx={styles.kpiLabel}>Original</Typography>
                <Typography sx={styles.kpiValue}>
                  {formatMoney(item.currency, item.originalAmount, amountsAreCents)}
                </Typography>
              </Box>

              <Box sx={styles.kpiBox}>
                <Typography variant="caption" sx={styles.kpiLabel}>Refunded</Typography>
                <Typography sx={styles.kpiValue}>
                  {formatMoney(item.currency, item.refundedAmountTotal, amountsAreCents)}
                </Typography>
              </Box>

              <Box sx={styles.kpiBox}>
                <Typography variant="caption" sx={styles.kpiLabel}>Remaining</Typography>
                <Typography sx={styles.kpiValue}>
                  {formatMoney(item.currency, remaining, amountsAreCents)}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Refund entries */}
            <Typography sx={{ fontWeight: 700, mb: 1 }}>
              Refund entries ({item.refunds?.length ?? 0})
            </Typography>

            <Stack spacing={1.25}>
              {(item.refunds ?? []).map((r) => (
                <Box key={r.refundPaymentId} sx={styles.refundRow}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, wordBreak: "break-all" }}>
                      {r.refundPaymentId}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Reason: {r.reason || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      By: {r.createdBy || "-"} • At: {formatDateTime(r.createdAt)}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontWeight: 900 }}>
                      {formatMoney(item.currency, r.amount, amountsAreCents)}
                    </Typography>
                    <Chip
                      size="small"
                      label={r.refund_status}
                      color={String(r.refund_status).toLowerCase() === "succeeded" ? "success" : "default"}
                      variant="outlined"
                      sx={{ mt: 0.75 }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handleDialogClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const styles = {
  kpiBox: {
    flex: 1,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    p: 2,
    backgroundColor: "background.paper",
  },
  kpiLabel: { color: "text.secondary" },
  kpiValue: { fontWeight: 800, fontSize: 16 },
  refundRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 2,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    p: 2,
    backgroundColor: "background.paper",
  },
} as const;